import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Chip,
  Tooltip,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import SaveIcon from '@mui/icons-material/Save';
import GroupsIcon from '@mui/icons-material/Groups';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import SettingsWorkspaceTabs, { resolveSettingsTab } from './SettingsWorkspaceTabs';
import TeamsSection from './TeamsSection';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

/**
 * Settings page — organisation management for the signed-in user.
 *
 * Three route-driven tabs:
 *   /settings          → Organisation (rename the workspace)
 *   /settings/members  → Members (who is in the org)
 *   /settings/teams    → Teams (groups + their notification addresses)
 *
 * Source of truth for the org: GET /api/users/me → response.organisation
 * (already loaded into AuthContext). After every mutation we refetch the
 * profile so the UI stays consistent with what the backend sees. Teams are
 * the exception — they have their own endpoint and manage their own state.
 */
const SettingsPage = () => {
  const { userProfile, organisation, fetchUserProfile } = useAuth();
  const isAdmin = userProfile?.org_role === 'admin';
  const { pathname } = useLocation();
  const tab = resolveSettingsTab(pathname);

  if (!organisation) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: 'primary.main' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 880, mx: 'auto', px: { xs: 2, sm: 4 }, py: 4 }}>
      {/* No per-tab subtitle here — the copy differed in length between tabs,
          so the tab strip shifted vertically on every switch. Each tab's card
          carries its own description instead. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <GroupsIcon sx={{ fontSize: 28, color: 'primary.main' }} />
        <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, letterSpacing: '-0.01em' }}>
          Organisation
        </Typography>
      </Box>

      <SettingsWorkspaceTabs />

      {tab === 'organisation' && (
        <OrganisationTab
          organisation={organisation}
          isAdmin={isAdmin}
          fetchUserProfile={fetchUserProfile}
        />
      )}
      {tab === 'members' && (
        <MembersTab
          organisation={organisation}
          userProfile={userProfile}
          isAdmin={isAdmin}
          fetchUserProfile={fetchUserProfile}
        />
      )}
      {tab === 'teams' && (
        <TeamsSection organisation={organisation} isAdmin={isAdmin} />
      )}
    </Box>
  );
};

/* ─── Organisation ──────────────────────────────────────────────────────── */

const OrganisationTab = ({ organisation, isAdmin, fetchUserProfile }) => {
  const [orgName, setOrgName] = useState(organisation?.name || '');
  const [savingName, setSavingName] = useState(false);
  const [nameOk, setNameOk] = useState('');

  // Keep the input in sync if the org loads after the first render.
  React.useEffect(() => {
    if (organisation?.name && orgName === '') setOrgName(organisation.name);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisation?.name]);

  const handleRenameOrg = async () => {
    if (!isAdmin || !orgName.trim() || orgName.trim() === organisation.name) return;
    setSavingName(true);
    setNameOk('');
    try {
      await axios.patch(
        `${API_BASE_URL}/api/organisations/${organisation.id}`,
        { name: orgName.trim() },
      );
      await fetchUserProfile();
      setNameOk('Saved');
      setTimeout(() => setNameOk(''), 2200);
    } catch (e) {
      setNameOk(e?.response?.data?.error || 'Save failed');
    } finally {
      setSavingName(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, mb: 0.5 }}>
        Name
      </Typography>
      <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', mb: 2 }}>
        Shown in the top nav and on shared content.
      </Typography>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
        <TextField
          value={orgName}
          onChange={(e) => setOrgName(e.target.value)}
          disabled={!isAdmin || savingName}
          size="small"
          sx={{ flex: 1, maxWidth: 420 }}
          placeholder="Acme Energy"
        />
        {isAdmin && (
          <Button
            variant="contained"
            size="small"
            onClick={handleRenameOrg}
            disabled={
              savingName ||
              !orgName.trim() ||
              orgName.trim() === organisation.name
            }
            startIcon={savingName ? <CircularProgress size={14} /> : <SaveIcon sx={{ fontSize: 16 }} />}
            sx={{ textTransform: 'none' }}
          >
            Save
          </Button>
        )}
        {nameOk && (
          <Typography sx={{ fontSize: '0.78rem', color: nameOk === 'Saved' ? 'primary.main' : 'error.main' }}>
            {nameOk}
          </Typography>
        )}
      </Box>
      {!isAdmin && (
        <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled', mt: 1.5 }}>
          Only organisation admins can rename the workspace.
        </Typography>
      )}
    </Paper>
  );
};

/* ─── Members ───────────────────────────────────────────────────────────── */

const MembersTab = ({ organisation, userProfile, isAdmin, fetchUserProfile }) => {
  const [newEmail, setNewEmail] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState('');
  const [addOk, setAddOk] = useState('');
  const [removingId, setRemovingId] = useState(null);
  const [removeError, setRemoveError] = useState('');

  const handleAddMember = async (e) => {
    e?.preventDefault();
    setAddError('');
    setAddOk('');
    if (!newEmail.trim()) return;
    setAdding(true);
    try {
      await axios.post(
        `${API_BASE_URL}/api/organisations/${organisation.id}/members`,
        { email: newEmail.trim().toLowerCase() },
      );
      setNewEmail('');
      setAddOk('Member added');
      await fetchUserProfile();
      setTimeout(() => setAddOk(''), 2200);
    } catch (e) {
      setAddError(e?.response?.data?.error || 'Could not add member');
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    setRemoveError('');
    setRemovingId(memberId);
    try {
      await axios.delete(
        `${API_BASE_URL}/api/organisations/${organisation.id}/members/${memberId}`,
      );
      await fetchUserProfile();
    } catch (e) {
      setRemoveError(e?.response?.data?.error || 'Could not remove member');
    } finally {
      setRemovingId(null);
    }
  };

  const members = organisation.members || [];

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography sx={{ fontSize: '0.95rem', fontWeight: 600 }}>
          Members
        </Typography>
        <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
          {members.length} {members.length === 1 ? 'person' : 'people'}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', mb: 2 }}>
        {isAdmin
          ? 'Add an existing Lumeros user by email. They must have already signed up.'
          : 'Only admins can add or remove members.'}
      </Typography>

      {isAdmin && (
        <Box
          component="form"
          onSubmit={handleAddMember}
          sx={{ display: 'flex', gap: 1.5, mb: 2.5, alignItems: 'flex-start' }}
        >
          <TextField
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            disabled={adding}
            size="small"
            sx={{ flex: 1, maxWidth: 420 }}
            placeholder="teammate@company.com"
          />
          <Button
            type="submit"
            variant="contained"
            size="small"
            disabled={adding || !newEmail.trim()}
            startIcon={adding ? <CircularProgress size={14} /> : <PersonAddAltIcon sx={{ fontSize: 16 }} />}
            sx={{ textTransform: 'none' }}
          >
            Add
          </Button>
        </Box>
      )}

      {addError && <Alert severity="error" sx={{ mb: 2 }}>{addError}</Alert>}
      {addOk && <Alert severity="success" sx={{ mb: 2 }}>{addOk}</Alert>}
      {removeError && <Alert severity="error" sx={{ mb: 2 }}>{removeError}</Alert>}

      <Divider sx={{ mb: 1 }} />

      <List disablePadding>
        {members.length === 0 && (
          <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', py: 2 }}>
            No members yet.
          </Typography>
        )}
        {members.map((m) => {
          const initials = (
            (m.first_name?.[0] || '') + (m.last_name?.[0] || '')
          ).toUpperCase() || (m.email?.[0] || '?').toUpperCase();
          const fullName =
            m.first_name && m.last_name
              ? `${m.first_name} ${m.last_name}`
              : (m.email || 'Unknown');
          const isYou = String(m.id) === String(userProfile?.id);
          const removable =
            isAdmin && !isYou && m.org_role !== 'admin'; // prevent removing fellow admins / self
          return (
            <ListItem
              key={m.id}
              disablePadding
              sx={{ py: 1.2, px: 0.5 }}
              secondaryAction={
                removable ? (
                  <Tooltip title="Remove from organisation">
                    <span>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleRemoveMember(m.id)}
                        disabled={removingId === m.id}
                        sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                      >
                        {removingId === m.id
                          ? <CircularProgress size={16} />
                          : <DeleteOutlineIcon sx={{ fontSize: 18 }} />}
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : null
              }
            >
              <ListItemAvatar>
                <Avatar sx={{
                  width: 36, height: 36,
                  bgcolor: m.org_role === 'admin' ? 'primary.main' : 'grey.700',
                  color: '#F4F2EC',
                  fontSize: '0.78rem', fontWeight: 700,
                }}>
                  {initials}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                      {fullName}
                    </Typography>
                    {isYou && (
                      <Chip
                        label="You"
                        size="small"
                        sx={{ height: 18, fontSize: '0.68rem', bgcolor: 'rgba(217, 138, 43, 0.15)', color: 'primary.main' }}
                      />
                    )}
                    {m.org_role === 'admin' && (
                      <Chip
                        label="Admin"
                        size="small"
                        sx={{ height: 18, fontSize: '0.68rem', bgcolor: 'rgba(233, 176, 101, 0.18)', color: '#b9a8ff' }}
                      />
                    )}
                  </Box>
                }
                secondary={
                  <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                    {m.email}
                  </Typography>
                }
              />
            </ListItem>
          );
        })}
      </List>
    </Paper>
  );
};

export default SettingsPage;
